import "@/App.css";
import { ModuleAccessProvider, useModuleAccess } from "./contexts/ModuleAccessContext";
import { MODULE_PATHS, MODULE_LANDINGS } from "./constants/moduleAccess";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { roleAllowed } from "./utils/roles";
import { Toaster } from "./components/ui/sonner";
import { useEnLinea } from "./pwa";   // Fix EU
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Finance from "./pages/Finance";
import Inventory from "./pages/Inventory";
import HR from "./pages/HR";
import Purchases from "./pages/Purchases";
import Budgets from "./pages/Budgets";
import Projects from "./pages/Projects";
import Settings from "./pages/Settings";
import UsersPage from "./pages/Users";
import AuditLog from "./pages/AuditLog";
import TesoreriaPagos from "./pages/TesoreriaPagos";
import TesoreriaCobranzas from "./pages/TesoreriaCobranzas";
import TesoreriaBancos from "./pages/TesoreriaBancos";
import TesoreriaTercerizados from "./pages/TesoreriaTercerizados";   // Fix EE (2026-09-24)
import ComprasServicios from "./pages/ComprasServicios";
import ComprasOperaciones from "./pages/ComprasOperaciones";
import ComprasCajaChica from "./pages/ComprasCajaChica";
import ComprasLibroCompras from "./pages/ComprasLibroCompras";
import FormateoPresupuestos from "./pages/compras/FormateoPresupuestos";
import VinculacionPedidosRapidos from "./pages/compras/VinculacionPedidosRapidos";   // Fix EF (2026-09-24)
import ComparativaAuxiliar from "./pages/compras/ComparativaAuxiliar";
import Clientes from "./pages/ingenieria/Clientes";
import Obras from "./pages/ingenieria/Obras";
// Fix Z (2026-09-02): Asistencias técnicas (órdenes de trabajo del operario).
import Asistencias from "./pages/ingenieria/Asistencias";
// Fix DT (2026-09-22): informes técnicos con IA y membrete de CEN.
import Informes from "./pages/ingenieria/Informes";
import TareasIngenieria from "./pages/ingenieria/TareasIngenieria";   // Fix EM (2026-09-28)
import FinanzasProyectado from "./pages/FinanzasProyectado";
import FinanzasPorObra from "./pages/FinanzasPorObra";
import FinanzasConsolidado from "./pages/FinanzasConsolidado";
import FinanzasGastosAdmin from "./pages/FinanzasGastosAdmin";
// Fix DS (2026-09-22): Contabilidad — plan de cuentas y libro de ventas.
import ContabilidadPlanCuentas from "./pages/ContabilidadPlanCuentas";
import ContabilidadLibroVentas from "./pages/ContabilidadLibroVentas";
import IngenieriaComputo from "./pages/IngenieriaComputo";
import IngenieriaLista from "./pages/IngenieriaLista";
import ProyectosArchivos from "./pages/ingenieria/ProyectosArchivos";
// Fix CL (2026-09-13): mapeo de capas/bloques de los planos a materiales del ERP.
import MapeoPlanos from "./pages/ingenieria/MapeoPlanos";
// Fix CM (2026-09-13): tareas y flujo de trabajo (todos los módulos).
import Tareas from "./pages/Tareas";
import MaterialsCatalog from "./pages/MaterialsCatalog";
import Ventas from "./pages/Ventas";
import ObraList from "./pages/obra/ObraList";
import ObraDetail from "./pages/obra/ObraDetail";
import ArchivosEmpresa from "./pages/ArchivosEmpresa";
import Marcar from "./pages/Marcar";   // Fix GX (2026-10-19): marcación del personal desde el celular (pública)

function LoadingScreen() {
  const enLinea = useEnLinea();   // Fix EU (2026-10-01)
  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <svg width="48" height="48" viewBox="0 0 40 40" fill="none" className="text-[#2E6DA4] animate-spin" style={{ animationDuration: "2s" }}>
          <ellipse cx="20" cy="20" rx="17" ry="7" stroke="currentColor" strokeWidth="1.5" transform="rotate(-30 20 20)" />
          <ellipse cx="20" cy="20" rx="17" ry="7" stroke="currentColor" strokeWidth="1.5" transform="rotate(30 20 20)" />
          <ellipse cx="20" cy="20" rx="17" ry="7" stroke="currentColor" strokeWidth="1.5" />
          <path d="M21.5 13L16 21.5H20.5L18.5 27L24 18.5H19.5L21.5 13Z" fill="currentColor" />
        </svg>
        <p className="text-sm text-gray-500 font-medium">Cargando CEN ERP...</p>
        {!enLinea && <p className="text-xs text-amber-600 font-semibold" data-testid="cargando-sin-conexion">Sin conexión. Se abre sola cuando vuelva la señal.</p>}
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <LoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Rutas permitidas para el rol Proyectista (sandbox estricto — Prompt 77)
const PROYECTISTA_PATHS = [
  "/ingenieria/proyecto",          // módulo Cómputo
  "/ingenieria/computo",           // módulo Proyecto
  "/ingenieria/proyectos/archivos",// Archivos Útiles
  "/ingenieria/mapeo-planos",      // Fix CL (2026-09-13): mapeo de capas y bloques
  "/tareas",                       // Fix CM (2026-09-13): sus tareas del flujo
  "/ingenieria/tareas",            // Fix EM (2026-09-28): tareas de Ingeniería
];

function ProyectistaRedirect() {
  useEffect(() => { toast.error("No tienes permiso para esa sección"); }, []);
  return <Navigate to="/ingenieria/proyecto" replace />;
}

// Rutas permitidas para "Residente de Obra" (acceso restringido — Prompt Bloque 2 Parte B)
// Fix Z (2026-09-02): + Ingeniería → Asistencias (el rol ya está en ASISTENCIA_ROLES del backend).
function isResidenteObraPathAllowed(pathname) {
  // Fix FH (2026-10-08): + Inventario → Alquileres.
  return pathname === "/compras/materiales" || pathname === "/obra" || pathname.startsWith("/obra/") || pathname === "/rrhh" || pathname === "/ingenieria/asistencias" || pathname.startsWith("/tareas") || pathname === "/inventario/alquileres";
}

function ResidenteObraRedirect() {
  useEffect(() => { toast.error("No tienes permiso para esa sección"); }, []);
  return <Navigate to="/compras/materiales" replace />;
}

// Rutas permitidas para "Operario Electricista" (Bloque 31 — sandbox estricto:
// SOLO puede operar en Compras > Materiales).
// Fix Z (2026-09-02): + Ingeniería → Asistencias (crea/carga/cierra sus propias OT).
function isOperarioElectricistaPathAllowed(pathname) {
  return pathname === "/compras/materiales" || pathname === "/ingenieria/asistencias" || pathname.startsWith("/tareas");
}

function OperarioElectricistaRedirect() {
  useEffect(() => { toast.error("No tienes permiso para esa sección"); }, []);
  return <Navigate to="/compras/materiales" replace />;
}

// Landing redirect: el Proyectista aterriza en su módulo; el Residente de Obra
// y el Operario Electricista en Compras > Materiales; el resto en el dashboard.
function HomeRedirect() {
  const { user } = useAuth();
  const { access, error } = useModuleAccess();
  if (error) return <p role="alert">No se pudo comprobar el acceso.</p>;
  if (!access) return <LoadingScreen />;
  if (access.custom) {
    const first = Object.keys(MODULE_PATHS).find(key => access.modules[key] === true);
    return first ? <Navigate to={MODULE_LANDINGS[first]} replace /> : <p role="alert">Tu rol no tiene módulos habilitados.</p>;
  }
  if (user?.role === "Proyectista") return <Navigate to="/ingenieria/proyecto" replace />;
  if (user?.role === "Residente de Obra") return <Navigate to="/compras/materiales" replace />;
  if (user?.role === "Operario Electricista") return <Navigate to="/compras/materiales" replace />;
  return <Navigate to="/dashboard" replace />;
}

// Role-based route guard: redirects to /dashboard if role not allowed
function RoleRoute({ allowedRoles, children }) {
  const { user } = useAuth();
  const { access } = useModuleAccess();
  const location = useLocation();
  // Proyectista: sandbox estricto — solo Cómputo / Proyecto / Archivos Útiles
  if (user?.role === "Proyectista") {
    const allowed = PROYECTISTA_PATHS.some((p) => location.pathname === p);
    if (!allowed) return <ProyectistaRedirect />;
    return children;
  }
  // Residente de Obra: sandbox estricto — solo Compras > Materiales / Obra
  if (user?.role === "Residente de Obra") {
    if (!isResidenteObraPathAllowed(location.pathname)) return <ResidenteObraRedirect />;
    return children;
  }
  // Bloque 31 — Operario Electricista: sandbox estricto, sólo Compras > Materiales.
  if (user?.role === "Operario Electricista") {
    if (!isOperarioElectricistaPathAllowed(location.pathname)) return <OperarioElectricistaRedirect />;
    return children;
  }
  if (access?.configured && ![R.USUARIOS, R.SUPERADMIN, R.ADMIN].includes(allowedRoles)) return children;
  if (!allowedRoles || roleAllowed(user?.role, allowedRoles)) return children;
  return <Navigate to="/dashboard" replace />;
}

// Role sets (matching NAV_TREE in Layout.jsx)
const R = {
  COMPRAS: ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Ingeniero Residente", "Compras", "Contador",
            "Director", "Gerencia Técnica", "Gerencia Financiera", "Jefe de Compras", "Jefe de Logística", "Residente de Obra"],
  TESORERIA: ["Superadministrador", "Administrador", "Gerente Tecnico", "Contador", "Compras",
              "Director", "Gerencia Técnica", "Gerencia Financiera"],
  FINANZAS: ["Superadministrador", "Administrador", "Gerente Tecnico", "Contador",
             "Director", "Gerencia Técnica", "Gerencia Financiera", "Accionista", "Gerencia Comercial"],
  // Fix DS (2026-09-22): Contabilidad la ven Contador, Gerencia Financiera y la
  // dirección; no entra Ventas ni el Accionista.
  CONTABILIDAD: ["Superadministrador", "Administrador", "Contador", "Gerencia Financiera",
                 "Gerente Tecnico", "Gerencia Técnica", "Director"],
  INGENIERIA: ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra",
               "Director", "Gerencia Técnica", "Jefe de Calidad", "Ingeniería", "Proyectista"],
  INVENTARIO: ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Ingeniero Residente", "Compras",
               "Director", "Gerencia Técnica", "Jefe de Compras", "Jefe de Logística", "Residente de Obra", "Ingeniería", "Pasantía"],
  VENTAS: ["Superadministrador", "Administrador", "Gerente Tecnico",
           "Director", "Gerencia Comercial", "Accionista"],
  OBRA: ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "Ingeniero Residente", "RRHH",
         "Director", "Gerencia Técnica", "Jefe de Calidad", "Jefe de RRHH", "Residente de Obra", "Ingeniería", "Pasantía"],
  RRHH: ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra", "RRHH",
         "Director", "Jefe de RRHH", "Residente de Obra"],
  ADMIN: ["Superadministrador", "Administrador"],
  SUPERADMIN: ["Superadministrador"],
  USUARIOS: ["Superadministrador", "Administrador", "Director"],   // Fix GU (2026-10-17): + Dirección General
  // Fix Z (2026-09-02): Ingeniería → Asistencias también para quienes las abren y cargan.
  ASISTENCIAS: ["Superadministrador", "Administrador", "Gerente Tecnico", "Coordinador de Obra",
                "Director", "Gerencia Técnica", "Jefe de Calidad", "Ingeniería", "Proyectista",
                "Residente de Obra", "Ingeniero Residente", "Jefe de Compras", "Operario Electricista"],
};

const appChildRoutes = (
  <>
            <Route index element={<HomeRedirect />} />
            <Route path="dashboard" element={<RoleRoute allowedRoles={undefined}><Dashboard /></RoleRoute>} />

            {/* COMPRAS */}
            <Route path="compras/materiales" element={<RoleRoute allowedRoles={R.COMPRAS}><Purchases /></RoleRoute>} />
            <Route path="compras/servicios" element={<RoleRoute allowedRoles={R.COMPRAS}><ComprasServicios /></RoleRoute>} />
            <Route path="compras/operaciones" element={<RoleRoute allowedRoles={R.COMPRAS}><ComprasOperaciones /></RoleRoute>} />
            <Route path="compras/caja-chica" element={<RoleRoute allowedRoles={R.COMPRAS}><ComprasCajaChica /></RoleRoute>} />
            <Route path="compras/libro-compras" element={<RoleRoute allowedRoles={R.COMPRAS}><ComprasLibroCompras /></RoleRoute>} />
            <Route path="compras/formateo" element={<RoleRoute allowedRoles={R.COMPRAS}><FormateoPresupuestos /></RoleRoute>} />
            <Route path="compras/vinculacion-pedidos-rapidos" element={<RoleRoute allowedRoles={["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Técnica", "Jefe de Compras", "Coordinador de Obra"]}><VinculacionPedidosRapidos /></RoleRoute>} />
            <Route path="compras/comparativa-auxiliar" element={<RoleRoute allowedRoles={["Superadministrador", "Administrador", "Gerente Tecnico", "Compras", "Gerencia Financiera"]}><ComparativaAuxiliar /></RoleRoute>} />

            {/* TESORERÍA */}
            {/* BLOQUE 34 — el click "OP → Tesorería" del dashboard de "Jefe de Compras" NO
                requirió ningún cambio de RBAC aquí: R.TESORERIA ya incluye el alias legacy
                "Compras" (role_utils.py / utils/roles.js, ROLE_ALIASES) que roleAllowed()
                normaliza al rol canónico "Jefe de Compras" — este rol YA tenía acceso
                completo a Pagos+Cobranzas desde el Bloque 15 (regla aditiva: "ningún rol que
                hoy accede a algo puede perder acceso"). Aprobar/rechazar OP sigue exigiendo
                Superadmin/Administrador/Gerente Tecnico (canApprove en TesoreriaPagos.jsx). */}
            <Route path="tesoreria/pagos" element={<RoleRoute allowedRoles={R.TESORERIA}><TesoreriaPagos /></RoleRoute>} />
            <Route path="tesoreria/cobranzas" element={<RoleRoute allowedRoles={R.TESORERIA}><TesoreriaCobranzas /></RoleRoute>} />
            <Route path="tesoreria/tercerizados" element={<RoleRoute allowedRoles={R.TESORERIA}><TesoreriaTercerizados /></RoleRoute>} />
            <Route path="tesoreria/procesos-bancarios" element={<RoleRoute allowedRoles={["Superadministrador", "Administrador", "Gerente Tecnico", "Gerencia Financiera", "Contador", "Director"]}><TesoreriaBancos /></RoleRoute>} />

            {/* FINANZAS */}
            <Route path="finanzas/real" element={<RoleRoute allowedRoles={R.FINANZAS}><Finance /></RoleRoute>} />
            <Route path="finanzas/proyectado" element={<RoleRoute allowedRoles={R.FINANZAS}><FinanzasProyectado /></RoleRoute>} />
            <Route path="finanzas/por-obra" element={<RoleRoute allowedRoles={R.FINANZAS}><FinanzasPorObra /></RoleRoute>} />
            <Route path="finanzas/consolidado" element={<RoleRoute allowedRoles={R.FINANZAS}><FinanzasConsolidado /></RoleRoute>} />
            <Route path="finanzas/gastos-admin" element={<RoleRoute allowedRoles={R.FINANZAS}><FinanzasGastosAdmin /></RoleRoute>} />
            <Route path="contabilidad/plan-cuentas" element={<RoleRoute allowedRoles={R.CONTABILIDAD}><ContabilidadPlanCuentas /></RoleRoute>} />
            <Route path="contabilidad/libro-compras" element={<RoleRoute allowedRoles={R.CONTABILIDAD}><ComprasLibroCompras /></RoleRoute>} />
            <Route path="contabilidad/libro-ventas" element={<RoleRoute allowedRoles={R.CONTABILIDAD}><ContabilidadLibroVentas /></RoleRoute>} />

            {/* Fix CM (2026-09-13): tareas y flujo de trabajo — todos los roles ven las suyas. */}
            <Route path="tareas" element={<Tareas />} />
            <Route path="tareas/:tareaId" element={<Tareas />} />

            {/* INGENIERÍA */}
            <Route path="ingenieria/proyecto" element={<RoleRoute allowedRoles={R.INGENIERIA}><Projects /></RoleRoute>} />
            <Route path="ingenieria/computo" element={<RoleRoute allowedRoles={R.INGENIERIA}><IngenieriaComputo /></RoleRoute>} />
            <Route path="ingenieria/proyectos/archivos" element={<RoleRoute allowedRoles={R.INGENIERIA}><ProyectosArchivos /></RoleRoute>} />
            {/* Fix CL (2026-09-13): mapeo de capas y bloques de los planos. */}
            <Route path="ingenieria/mapeo-planos" element={<RoleRoute allowedRoles={R.INGENIERIA}><MapeoPlanos /></RoleRoute>} />
            <Route path="ingenieria/catalogo-materiales" element={<RoleRoute allowedRoles={R.INGENIERIA}><MaterialsCatalog /></RoleRoute>} />
            <Route path="ingenieria/clientes" element={<RoleRoute allowedRoles={R.INGENIERIA}><Clientes /></RoleRoute>} />
            <Route path="ingenieria/obras" element={<RoleRoute allowedRoles={R.INGENIERIA}><Obras /></RoleRoute>} />
            <Route path="ingenieria/asistencias" element={<RoleRoute allowedRoles={R.ASISTENCIAS}><Asistencias /></RoleRoute>} />
            <Route path="ingenieria/informes" element={<RoleRoute allowedRoles={R.INGENIERIA}><Informes /></RoleRoute>} />
            <Route path="ingenieria/tareas" element={<RoleRoute allowedRoles={R.INGENIERIA}><TareasIngenieria /></RoleRoute>} />
            <Route path="ingenieria/presupuestos/obra" element={<RoleRoute allowedRoles={R.INGENIERIA}><Budgets /></RoleRoute>} />
            <Route path="ingenieria/presupuestos/proyecto" element={<RoleRoute allowedRoles={R.INGENIERIA}><Budgets /></RoleRoute>} />
            <Route path="ingenieria/presupuestos/varios" element={<RoleRoute allowedRoles={R.INGENIERIA}><Budgets /></RoleRoute>} />
            <Route path="ingenieria/presupuestos" element={<RoleRoute allowedRoles={R.INGENIERIA}><Budgets /></RoleRoute>} />
            <Route path="ingenieria/lista" element={<RoleRoute allowedRoles={R.INGENIERIA}><IngenieriaLista /></RoleRoute>} />

            {/* INVENTARIO */}
            <Route path="inventario/materiales" element={<RoleRoute allowedRoles={R.INVENTARIO}><Inventory defaultTab="materiales" /></RoleRoute>} />
            <Route path="inventario/herramientas" element={<RoleRoute allowedRoles={R.INVENTARIO}><Inventory defaultTab="herramientas" /></RoleRoute>} />
            <Route path="inventario/alquileres" element={<RoleRoute allowedRoles={R.INVENTARIO}><Inventory defaultTab="alquileres" /></RoleRoute>} />

            {/* VENTAS / OBRA / RRHH */}
            <Route path="ventas" element={<RoleRoute allowedRoles={R.VENTAS}><Ventas /></RoleRoute>} />
            <Route path="obra" element={<RoleRoute allowedRoles={R.OBRA}><ObraList /></RoleRoute>} />
            <Route path="obra/:id" element={<RoleRoute allowedRoles={R.OBRA}><ObraDetail /></RoleRoute>} />
            <Route path="rrhh" element={<RoleRoute allowedRoles={R.RRHH}><HR /></RoleRoute>} />
            <Route path="archivos" element={<RoleRoute allowedRoles={undefined}><ArchivosEmpresa /></RoleRoute>} />

            {/* ADMIN */}
            <Route path="usuarios" element={<RoleRoute allowedRoles={R.USUARIOS}><UsersPage /></RoleRoute>} />
            <Route path="auditoria" element={<RoleRoute allowedRoles={R.SUPERADMIN}><AuditLog /></RoleRoute>} />
            <Route path="configuracion" element={<RoleRoute allowedRoles={undefined}><Settings /></RoleRoute>} />

            {/* Legacy redirects */}
            <Route path="finance" element={<Navigate to="/finanzas/real" replace />} />
            <Route path="inventory" element={<Navigate to="/inventario/materiales" replace />} />
            <Route path="hr" element={<Navigate to="/rrhh" replace />} />
            <Route path="purchases" element={<Navigate to="/compras/materiales" replace />} />
            <Route path="budgets" element={<Navigate to="/ingenieria/presupuestos" replace />} />
            <Route path="projects" element={<Navigate to="/ingenieria/proyecto" replace />} />
            <Route path="users" element={<Navigate to="/usuarios" replace />} />
            <Route path="audit" element={<Navigate to="/auditoria" replace />} />
            <Route path="settings" element={<Navigate to="/configuracion" replace />} />
  </>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ModuleAccessProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Fix GX (2026-10-19): marcación del personal con geolocalización. Pública: el trabajador entra con cédula y PIN. */}
          <Route path="/marcar" element={<Marcar />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout tabRoutes={appChildRoutes} />
              </ProtectedRoute>
            }
          >
            {appChildRoutes}
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </ModuleAccessProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
